// src/components/DailySchedule.js
// Componente para mostrar el horario diario de citas

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge, Modal, Table, Spinner } from 'react-bootstrap';
import { getGroomingItems } from '../services/appointmentApi';
import { getAppointmentDetails } from '../services/appointmentDetailApi';
import './DailyScheduleModern.css';

const DailySchedule = ({
  date,
  appointments = [],
  /** Slots ocupados por otros (solo indicador, sin detalle) - vista groomer */
  occupiedSlots = [],
  onEditAppointment,
  onDeleteAppointment,
  onConsentClick,
  onReceiptClick,
  onSlotClick,
  consentByAppointmentId = {}
}) => {
  // Estado para almacenar el mapa de items por id_item
  const [itemsMap, setItemsMap] = useState({});
  // Estado para el modal de servicios
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentServices, setAppointmentServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Cargar items de grooming al montar el componente
  useEffect(() => {
    const loadItems = async () => {
      try {
        const { ok, data } = await getGroomingItems();
        
        if (ok && data?.code === 200) {
          const responseData = data.data || data.message || [];
          const items = Array.isArray(responseData) ? responseData : [];
          
          // Crear un mapa de items por id_item para acceso rápido
          const map = {};
          items.forEach(item => {
            if (item.id_item) {
              map[item.id_item] = item;
            }
          });
          
          setItemsMap(map);
        }
      } catch (error) {
        console.error('Error cargando items de grooming:', error);
      }
    };

    loadItems();
  }, []);
  // Formatear precio con signo de peso colombiano
  const formatPrice = (price) => {
    if (!price && price !== 0) return '';
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return '';
    return `$${numPrice.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Generar slots de tiempo de 9:00 AM a 6:00 PM cada 30 minutos
  const generateTimeSlots = () => {
    const slots = [];
    let currentTime = 9 * 60; // 9:00 AM en minutos
    
    while (currentTime <= 18 * 60) { // Hasta 6:00 PM (18:00)
      const hours = Math.floor(currentTime / 60);
      const minutes = currentTime % 60;
      const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      
      slots.push({
        time: timeStr,
        value: currentTime,
        displayTime: timeStr,
        isLunchTime: false
      });
      
      currentTime += 30; // Incrementar 30 minutos
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Obtener citas para un slot específico
  const getAppointmentsForSlot = (slotTime) => {
    return appointments.filter(apt => {
      if (!apt.start_at) return false;
      
      const appointmentTime = new Date(apt.start_at);
      const appointmentMinutes = appointmentTime.getHours() * 60 + appointmentTime.getMinutes();
      
      // Verificar si la cita está en este slot de 30 minutos
      return appointmentMinutes >= slotTime.value && 
             appointmentMinutes < slotTime.value + 30;
    });
  };

  // Formatear fecha para mostrar
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    // Crear la fecha correctamente desde el string YYYY-MM-DD
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, month - 1, day); // month - 1 porque los meses son 0-indexados
    
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    
    return date.toLocaleDateString('es-ES', options);
  };

  // Obtener color del badge según el estado
  const getStatusColor = (statusId) => {
    const status = (statusId || '').toUpperCase();
    switch (status) {
      case "PROGRAMADO": return 'secondary'; // Programada
      case "CONFIRMADO": return 'primary';   // Confirmada
      case "EN_PROCESO":
      case "EN PROCESO": return 'warning';   // En Proceso
      case "COMPLETADO": return 'success';   // Completada
      case "CANCELADO": return 'danger';    // Cancelada
      case "PAGADA": return 'success';      // Pagada (verde como completado)
      default: return 'secondary';
    }
  };

  // Obtener nombre del estado
  const getStatusName = (statusId) => {
    if (!statusId) return 'Desconocido';
    const status = String(statusId).toUpperCase().trim();
    switch (status) {
      case "PROGRAMADO": return 'Programada';
      case "CONFIRMADO": return 'Confirmada';
      case "EN_PROCESO":
      case "EN PROCESO": return 'En Proceso';
      case "COMPLETADO": return 'Completado';
      case "CANCELADO": return 'Cancelado';
      case "PAGADA": return 'Pagada';
      default: return statusId; // Usar el nombre del backend si no está mapeado
    }
  };

  // Obtener icono según el estado
  const getStatusIcon = (statusId) => {
    const status = (statusId || '').toUpperCase();
    switch (status) {
      case "PROGRAMADO": return 'fa-calendar-check';
      case "CONFIRMADO": return 'fa-check-circle';
      case "EN_PROCESO":
      case "EN PROCESO": return 'fa-spinner';
      case "COMPLETADO": return 'fa-check-double';
      case "CANCELADO": return 'fa-times-circle';
      case "PAGADA": return 'fa-money-bill-wave';
      default: return 'fa-question-circle';
    }
  };

  // Abrir modal de servicios de la cita
  const handleShowServices = async (appointment) => {
    setSelectedAppointment(appointment);
    setShowServicesModal(true);
    setLoadingServices(true);
    setAppointmentServices([]);

    try {
      const { ok, data } = await getAppointmentDetails(appointment.id_appointment);
      
      if (ok && data?.code === 200) {
        const responseData = data.data || data.message || [];
        const services = Array.isArray(responseData) ? responseData : [];
        setAppointmentServices(services);
      } else {
        // Si no hay servicios en el detalle, mostrar el servicio principal de la cita
        if (appointment.id_item) {
          const mainItem = itemsMap[appointment.id_item];
          if (mainItem) {
            setAppointmentServices([{
              id_item: appointment.id_item,
              description: mainItem.description || mainItem.name || 'Servicio',
              quantity: 1,
              unit_price: mainItem.price || 0
            }]);
          }
        }
      }
    } catch (error) {
      console.error('Error cargando servicios de la cita:', error);
      // Fallback: mostrar servicio principal si existe
      if (appointment.id_item) {
        const mainItem = itemsMap[appointment.id_item];
        if (mainItem) {
          setAppointmentServices([{
            id_item: appointment.id_item,
            description: mainItem.description || mainItem.name || 'Servicio',
            quantity: 1,
            unit_price: mainItem.price || 0
          }]);
        }
      }
    } finally {
      setLoadingServices(false);
    }
  };

  // Cerrar modal de servicios
  const handleCloseServicesModal = () => {
    setShowServicesModal(false);
    setSelectedAppointment(null);
    setAppointmentServices([]);
  };

  return (
    <Card className="modern-schedule-card shadow-sm">
      <Card.Header className="bg-gradient-schedule text-white">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1 fw-bold">
              <i className="fas fa-calendar-day me-2"></i>
              Agenda del Día
            </h5>
            <small className="opacity-75">
              {formatDate(date)}
            </small>
          </div>
          <div className="text-end">
            <Badge bg="light" text="dark" className="schedule-badge">
              <i className="fas fa-paw me-1"></i>
              {appointments.length} cita{appointments.length !== 1 ? 's' : ''}
            </Badge>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        <div className="modern-daily-schedule">
          {timeSlots.map((slot, index) => {
            const slotAppointments = getAppointmentsForSlot(slot);
            const hasAppointments = slotAppointments.length > 0;
            const isOccupiedByOther = !hasAppointments && occupiedSlots.includes(slot.displayTime);

            return (
              <div
                key={index}
                className={`modern-schedule-row ${
                  slot.isLunchTime ? 'lunch-slot' : hasAppointments ? 'has-appointments' : isOccupiedByOther ? 'occupied-slot' : 'available-slot'
                }`}
                style={{
                  animationDelay: `${index * 0.05}s`
                }}
              >
                {/* Timeline indicator */}
                <div className="timeline-container">
                  <div className={`timeline-dot ${
                    slot.isLunchTime ? 'lunch' : hasAppointments ? 'active' : isOccupiedByOther ? 'occupied' : 'inactive'
                  }`}>
                    <i className={`fas ${
                      slot.isLunchTime ? 'fa-utensils' :
                      hasAppointments ? 'fa-paw' : isOccupiedByOther ? 'fa-user-clock' : 'fa-clock'
                    }`}></i>
                  </div>
                  {index < timeSlots.length - 1 && (
                    <div className={`timeline-line ${hasAppointments ? 'active' : ''}`}></div>
                  )}
                </div>
                
                {/* Time label */}
                <div className="time-label">
                  <div className="time-text">{slot.displayTime}</div>
                  <div className="time-period">
                    {parseInt(slot.displayTime.split(':')[0]) < 12 ? 'AM' : 'PM'}
                  </div>
                </div>
                
                {/* Content area */}
                <div className="slot-content">
                  {slot.isLunchTime ? (
                    <div className="lunch-break-card">
                      <div className="lunch-icon-wrapper">
                        <i className="fas fa-utensils"></i>
                      </div>
                      <div className="lunch-text">
                        <strong>Horario de Almuerzo</strong>
                        <small>No disponible para citas</small>
                      </div>
                    </div>
                  ) : isOccupiedByOther ? (
                    <div className="occupied-slot-card">
                      <i className="fas fa-user-clock me-2 text-muted"></i>
                      <span className="text-muted">Ocupado</span>
                    </div>
                  ) : hasAppointments ? (
                    <div className="appointments-container">
                      {slotAppointments.map((appointment, aptIndex) => (
                        <div
                          key={appointment.id_appointment}
                          className={`modern-appointment-card status-${getStatusColor(appointment.status_name)}`}
                          style={{
                            animationDelay: `${(index * 0.05) + (aptIndex * 0.1)}s`,
                            borderLeftColor: appointment.groomer_color || undefined,
                            borderLeftWidth: appointment.groomer_color ? '4px' : undefined
                          }}
                        >
                          {/* Barra de color del groomer o estado */}
                          <div
                            className={`status-bar ${getStatusColor(appointment.status_name)}`}
                            style={{ backgroundColor: appointment.groomer_color || undefined }}
                          ></div>
                          
                          {/* Pet info */}
                          <div className="appointment-header">
                            <div className="pet-avatar">
                              <i className="fas fa-paw"></i>
                            </div>
                            <div className="pet-info">
                              <h6 className="pet-name">{appointment.pet_name || 'Sin nombre'}</h6>
                              <Badge 
                                bg={getStatusColor(appointment.status_name)}
                                className="status-badge-modern"
                              >
                                <i className={`fas ${getStatusIcon(appointment.status_name)} me-1`}></i>
                                {getStatusName(appointment.status_name)}
                              </Badge>
                            </div>
                          </div>
                          
                          {/* Appointment details */}
                          <div className="appointment-details">
                            <div className="detail-item">
                              <i className="fas fa-user text-primary"></i>
                              <span>{appointment.client_name || 'N/A'}</span>
                            </div>
                            <div className="detail-item">
                              <i className="fab fa-whatsapp text-success"></i>
                              <span>{appointment.phone || 'N/A'}</span>
                            </div>
                            <div className="detail-item">
                              <i className="fas fa-cut text-info"></i>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-start text-decoration-none"
                                onClick={() => handleShowServices(appointment)}
                                style={{ 
                                  color: 'inherit',
                                  fontWeight: 'normal',
                                  textAlign: 'left',
                                  padding: 0,
                                  border: 'none',
                                  background: 'none'
                                }}
                              >
                                Ver servicios
                                <i className="fas fa-chevron-right ms-1" style={{ fontSize: '0.75em' }}></i>
                              </Button>
                            </div>
                            <div className="detail-item">
                              <i className="fas fa-hourglass-half text-warning"></i>
                              <span>{appointment.duration_min || 60} minutos</span>
                            </div>
                            {appointment.groomer_name && (
                              <div className="detail-item">
                                <span
                                  className="badge rounded-pill me-1"
                                  style={{ backgroundColor: appointment.groomer_color || '#6c757d', color: '#fff' }}
                                  title="Groomer"
                                >
                                  {appointment.groomer_name}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Fotos del servicio */}
                          {(appointment.url_image_before || appointment.url_image_after) && (
                            <div className="appointment-photos mt-3">
                              <div className="photos-header mb-2">
                                <i className="fas fa-camera me-2 text-info"></i>
                                <strong>Fotos del Servicio</strong>
                              </div>
                              <div className="photos-grid">
                                {appointment.url_image_before && (
                                  <div className="photo-container">
                                    <div className="photo-label">
                                      <i className="fas fa-dog me-1"></i>
                                      Antes
                                    </div>
                                    <img
                                      src={appointment.url_image_before}
                                      alt="Antes del baño"
                                      className="appointment-photo"
                                      onClick={() => window.open(appointment.url_image_before, '_blank')}
                                    />
                                  </div>
                                )}
                                {appointment.url_image_after && (
                                  <div className="photo-container">
                                    <div className="photo-label">
                                      <i className="fas fa-spa me-1"></i>
                                      Después
                                    </div>
                                    <img
                                      src={appointment.url_image_after}
                                      alt="Después del baño"
                                      className="appointment-photo"
                                      onClick={() => window.open(appointment.url_image_after, '_blank')}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Action buttons */}
                          <div className="appointment-actions">
                            {onConsentClick && (
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="modern-action-btn"
                                onClick={() => {
                                  const consent = consentByAppointmentId[appointment.id_appointment];
                                  if (consent?.url_pdf_firebase) {
                                    window.open(consent.url_pdf_firebase, '_blank');
                                  } else {
                                    onConsentClick(appointment);
                                  }
                                }}
                                title={consentByAppointmentId[appointment.id_appointment]?.url_pdf_firebase ? 'Descargar consentimiento' : 'Generar consentimiento'}
                              >
                                <i className={consentByAppointmentId[appointment.id_appointment]?.url_pdf_firebase ? 'fas fa-file-pdf me-1' : 'fas fa-file-signature me-1'}></i>
                                {consentByAppointmentId[appointment.id_appointment]?.url_pdf_firebase ? 'Descargar consentimiento' : 'Generar consentimiento'}
                              </Button>
                            )}
                            {onReceiptClick && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="modern-action-btn"
                                onClick={() => onReceiptClick(appointment)}
                                title="Descargar recibo de pago (PDF 80mm)"
                              >
                                <i className="fas fa-receipt me-1"></i>
                                Recibo
                              </Button>
                            )}
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="modern-action-btn"
                              onClick={() => onEditAppointment(appointment)}
                            >
                              <i className="fas fa-edit me-1"></i>
                              Editar
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="modern-action-btn"
                              onClick={() => onDeleteAppointment(appointment)}
                            >
                              <i className="fas fa-trash me-1"></i>
                              Eliminar
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : !isOccupiedByOther ? (
                    <div
                      className="available-slot-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => onSlotClick?.(slot.displayTime)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSlotClick?.(slot.displayTime); }}
                      style={{ cursor: onSlotClick ? 'pointer' : undefined }}
                    >
                      <i className="fas fa-circle text-success me-2 pulse-dot"></i>
                      <span>Horario disponible para agendar</span>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </Card.Body>

      {/* Modal de servicios de la cita */}
      <Modal show={showServicesModal} onHide={handleCloseServicesModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-list me-2"></i>
            Servicios de la Cita
            {selectedAppointment?.pet_name && (
              <span className="text-muted fw-normal ms-2">
                — {selectedAppointment.pet_name}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingServices ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" />
            </div>
          ) : appointmentServices.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="fas fa-inbox fa-2x mb-3"></i>
              <p>No hay servicios registrados para esta cita.</p>
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Servicio</th>
                  <th className="text-center">Cantidad</th>
                  <th className="text-end">Precio Unitario</th>
                  <th className="text-end">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {appointmentServices.map((service, idx) => {
                  const item = itemsMap[service.id_item] || {};
                  const description = service.description || item.description || item.name || 'Servicio';
                  const unitPrice = service.unit_price || item.price || 0;
                  const quantity = service.quantity || 1;
                  const subtotal = unitPrice * quantity;

                  return (
                    <tr key={`${service.id_item}-${idx}`}>
                      <td>{description}</td>
                      <td className="text-center">{quantity}</td>
                      <td className="text-end">{formatPrice(unitPrice)}</td>
                      <td className="text-end fw-bold">{formatPrice(subtotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="table-active">
                  <th colSpan="3" className="text-end">Total:</th>
                  <th className="text-end">
                    {formatPrice(
                      appointmentServices.reduce((sum, service) => {
                        const unitPrice = service.unit_price || itemsMap[service.id_item]?.price || 0;
                        const quantity = service.quantity || 1;
                        return sum + (unitPrice * quantity);
                      }, 0)
                    )}
                  </th>
                </tr>
              </tfoot>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseServicesModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default DailySchedule;
